import {useMutation} from "@apollo/client";
import {yupResolver} from "@hookform/resolvers/yup";
import {useContext, useEffect, useRef, useState} from "react";
import {OverlayTrigger, Tooltip} from "react-bootstrap";
import {CopyToClipboard} from "react-copy-to-clipboard";
import {Controller, useForm} from "react-hook-form";
import {FiCheck, FiCopy} from "react-icons/fi";
import * as Yup from "yup";

import {NewCheckVaultItemsContext} from "@/components/context/NewCheckVaultItemsContextInterface.tsx";
import {Icon} from "@/components/icon/Icon";
import {InputGroup} from "@/components/inputGroup/InputGroup";
import {
  GQL_SERVICE_VARIABLES_GROUP_BY_CREDENTIAL_ID_QUERY,
  GQL_SERVICE_VARIABLES_UPDATE,
} from "@/lib/queries/serviceVariables.ts";
import {ResponseStatus} from "@/lib/types/common.ts";
import {CredentialPropertyLabels} from "@/lib/types/credentials.ts";
import {ServiceVariable, ServiceVariableMutationResponse} from "@/lib/types/serviceVariables.ts";

import styles from "./VaultItemServiceVariableInput.module.scss";

interface VaultItemServiceVariableProps {
  serviceVariable: ServiceVariable;
}

interface VaultItemServiceVariableInputForm {
  serviceVariableName: string;
}

const VaultItemServiceVariableInputSchema = Yup.object({
  serviceVariableName: Yup.string()
    .matches(/^[a-zA-Z0-9_]*$/, "Variable name can only contain letters, numbers, and underscores.")
    .required("This field is required."),
});

export const VaultItemServiceVariableInput = ({serviceVariable}: VaultItemServiceVariableProps) => {
  const [oldServiceVariableValue, setOldServiceVariableValue] = useState<string>(serviceVariable.variable_name);
  const {checkId: contextCheckId, updateVaultServiceVariableName} = useContext(NewCheckVaultItemsContext);
  const [updateServiceVariable] = useMutation<ServiceVariableMutationResponse>(GQL_SERVICE_VARIABLES_UPDATE, {
    refetchQueries: [
      {
        query: GQL_SERVICE_VARIABLES_GROUP_BY_CREDENTIAL_ID_QUERY,
        variables: {
          service_id: contextCheckId,
        },
      },
    ],
  });
  const serviceVariableLabel = CredentialPropertyLabels[serviceVariable.property_name];
  const timerRef = useRef<ReturnType<typeof setTimeout>>();
  const [showCopied, setShowCopied] = useState(false);

  const {
    handleSubmit,
    control,
    setError,
    formState: {errors},
  } = useForm<VaultItemServiceVariableInputForm>({
    resolver: yupResolver<VaultItemServiceVariableInputForm>(VaultItemServiceVariableInputSchema),
    defaultValues: {
      serviceVariableName: serviceVariable.variable_name,
    },
    mode: "onBlur",
    shouldFocusError: false,
  });

  const onSubmit = async (data: VaultItemServiceVariableInputForm) => {
    const serviceVariableName = data.serviceVariableName;
    if (serviceVariableName === oldServiceVariableValue) {
      return;
    }
    if (!contextCheckId) {
      const updateSuccess = updateVaultServiceVariableName(
        serviceVariable.credential.id,
        serviceVariable.property_name,
        serviceVariableName
      );
      if (updateSuccess) {
        setOldServiceVariableValue(serviceVariableName);
      }
      return;
    }
    const response = await updateServiceVariable({
      variables: {
        id: serviceVariable.id,
        input: {
          credential_id: serviceVariable.credential.id,
          property_name: serviceVariable.property_name,
          variable_name: serviceVariableName,
        },
      },
    });
    if (response.data?.response.status === ResponseStatus.ERROR) {
      if (response.data?.response.message.non_field_errors[0]) {
        setError("serviceVariableName", {message: response.data?.response.message.non_field_errors[0]});
      }
    } else {
      setOldServiceVariableValue(serviceVariableName);
    }
  };

  useEffect(() => {
    return () => {
      clearTimeout(timerRef.current);
    };
  }, []);

  return (
    <form onBlur={handleSubmit(onSubmit)}>
      <div className={"mb-3"}>
        <div className={"d-flex justify-content-between pulse-color-gray-700 position-relative w-100"}>
          <div className="d-flex align-items-center">
            <div className="pulseTextBodySmall pulse-color-gray-900">{serviceVariableLabel}</div>
            <OverlayTrigger
              placement="top"
              delay={{show: 500, hide: 200}}
              overlay={(props) => <Tooltip {...props}>Copy this Variable</Tooltip>}
            >
              <CopyToClipboard
                text={`$${oldServiceVariableValue}$`}
                onCopy={() => {
                  setShowCopied(true);
                  timerRef.current = setTimeout(() => {
                    setShowCopied(false);
                  }, 2000);
                }}
              >
                <div className={"d-flex ms-2 align-items-center"}>
                  <Icon icon={FiCopy} className={"pulse-color-gray-500 cursor-pointer"} />
                </div>
              </CopyToClipboard>
            </OverlayTrigger>
          </div>
          {showCopied && (
            <div className={`${styles.vaultIcon} position-absolute end-0 bottom-0 mb-1`}>
              <Icon icon={FiCheck} removeDivDimensions /> Copied
            </div>
          )}
        </div>
        <InputGroup startAddon={"$"} endAddon={"$"}>
          <Controller
            name={"serviceVariableName"}
            control={control}
            render={({field}) => <input {...field} type={"text"} className={"form-control"} />}
          />
        </InputGroup>
        {errors.serviceVariableName && (
          <div className="invalid-feedback d-block">{errors.serviceVariableName?.message}</div>
        )}
      </div>
    </form>
  );
};
